import * as ko from "knockout";
import {IRuleset} from "../../../index";

/**
 * Name + target-collision state for one ruleset row in a copy wizard.
 * Optional {@link nameFormatValidator} lets a host wizard apply the same “strong” checks as
 * {@link component/ruleset/mutable/mutableRuleset.ts} `errName` without changing the standalone ruleset wizard.
 */
export class RulesetNameConflictRowVm {
    readonly hadConflictInitially: boolean;
    readonly hadInitialBatchDuplicateName: boolean;
    readonly draftName: KnockoutObservable<string>;

    constructor(
        readonly ruleset: IRuleset,
        private readonly getTargetRulesets: () => IRuleset[],
        private readonly getOtherDraftNames: () => string[],
        hadInitialBatchDuplicateName: boolean,
        private readonly nameFormatValidator?: (trimmedName: string) => boolean,
    ) {
        this.draftName = ko.observable(ruleset.name);
        const sourceNameTrimmed = ruleset.name.trim();
        this.hadConflictInitially = this.getTargetRulesets().some(
            (t) => t.name.trim() === sourceNameTrimmed,
        );
        this.hadInitialBatchDuplicateName = hadInitialBatchDuplicateName;
    }

    readonly conflictTarget = ko.pureComputed((): IRuleset | null => {
        const trimmed = this.draftName().trim();
        return this.getTargetRulesets().find((t) => t.name.trim() === trimmed) ?? null;
    });

    readonly hasFormatViolation = ko.pureComputed((): boolean => {
        const trimmed = this.draftName().trim();
        if (!trimmed) {
            return false;
        }
        if (this.nameFormatValidator) {
            return !this.nameFormatValidator(trimmed);
        }
        return false;
    });

    readonly hasBlockingConflict = ko.pureComputed((): boolean => {
        const my = this.draftName().trim();
        if (!my) {
            return true;
        }
        if (this.nameFormatValidator && !this.nameFormatValidator(my)) {
            return true;
        }
        if (this.getTargetRulesets().some((t) => t.name.trim() === my)) {
            return true;
        }
        return this.getOtherDraftNames().includes(my);
    });

    readonly badgeState = ko.pureComputed((): "conflict" | "noconflict" =>
        this.hasBlockingConflict() ? "conflict" : "noconflict",
    );

    readonly showNameInput = ko.pureComputed(
        () =>
            this.hasBlockingConflict() ||
            this.hadConflictInitially ||
            this.hadInitialBatchDuplicateName ||
            this.hasFormatViolation(),
    );
}
